<template>
  <div class="translation-config-tabs">
    <!-- Loading Overlay -->
    <div v-if="isLoading" class="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center bg-dark bg-opacity-50">
      <div class="text-center">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <p class="mt-3 text-white">{{ loadingMessage }}</p>
      </div>
    </div>

    <!-- Action Bar -->
    <div class="d-flex justify-content-start mb-4">
      <button
        @click="openNewLanguageModal"
        :disabled="isLoading"
        class="btn btn-success me-2 d-flex align-items-center mr-1"
      >
        <i class="bi bi-plus-circle me-1"></i>
        {{ $t('TRANSLATION_CONFIG.NewLanguage') }}
      </button>
      <button
        @click="saveAllLanguages"
        :disabled="isLoading"
        class="btn btn-primary d-flex align-items-center"
      >
        <i class="bi bi-save me-1"></i>
        {{ $t('TRANSLATION_CONFIG.Save') }}
      </button>
    </div>

    <!-- Tabs Header -->
    <ul class="nav nav-tabs mb-4">
      <li class="nav-item" v-for="(lang, index) in languages" :key="lang.code">
        <button
          class="nav-link"
          :class="{ active: activeTab === index }"
          @click="activeTab = index"
        >
          {{ lang.label }}
        </button>
      </li>
    </ul>

    <!-- Tab Content -->
    <div v-if="languages[activeTab]" class="card">
      <div class="card-header d-flex justify-content-between align-items-center">
        <h5 class="mb-0">
          {{ $t('TRANSLATION_CONFIG.TermsIn') }} {{ languages[activeTab].label }}
        </h5>
        <div>
          <button
            @click="deleteLanguage"
            class="btn btn-outline-danger btn-sm me-2"
            v-if="languages.length > 1"
          >
            <i class="bi bi-trash"></i> {{ $t('TRANSLATION_CONFIG.DeleteLanguage') }}
          </button>
          <button
            @click="addTerm"
            class="btn btn-primary btn-sm"
          >
            <i class="bi bi-plus"></i> {{ $t('TRANSLATION_CONFIG.AddTerm') }}
          </button>
        </div>
      </div>
      <div class="card-body">
        <!-- Words List Component -->
        <words-list 
          :words="languages[activeTab].config ? languages[activeTab].config.words : languages[activeTab].words" 
          @remove="removeTerm"
        />
      </div>
    </div>

    <!-- New Language Modal -->
    <new-language-modal 
      :visible="showNewLanguageModal"
      @save="addNewLanguage"
      @close="showNewLanguageModal = false"
    />

    <!-- Delete Language Modal -->
    <confirmation-modal
      :visible="showDeleteModal"
      :title="$t('TRANSLATION_CONFIG.DeleteLanguage')"
      :message="$t('TRANSLATION_CONFIG.ConfirmDelete')"
      :confirm-text="$t('TRANSLATION_CONFIG.Remove')"
      :cancel-text="$t('TRANSLATION_CONFIG.Cancel')"
      @confirm="confirmDeleteLanguage"
      @cancel="showDeleteModal = false"
    />
  </div>
</template>

<script>
import translationConfigService from '@/providers/translationConfigService'
import WordsList from './WordsList.vue'
import NewLanguageModal from './NewLanguageModal.vue'
import ConfirmationModal from './ConfirmationModal.vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  name: 'TranslationTabs',
  components: {
    WordsList,
    NewLanguageModal,
    ConfirmationModal
  },
  data() {
    return {
      languages: [],
      activeTab: 0,
      showDeleteModal: false,
      showNewLanguageModal: false,
      isLoading: false,
      loadingMessage: ''
    }
  },
  mounted() {
    this.loadLanguages()
  },
  methods: {
    async loadLanguages() {
      this.isLoading = true
      this.loadingMessage = this.$t('TRANSLATION_CONFIG.Loading') || 'Carregando...'
      try {
        const response = await translationConfigService.getAllTranslationConfigs()
        const languagesData = Array.isArray(response) ? response : (response.data || [])
        this.languages = languagesData.map(lang => ({
          ...lang,
          _id: lang._id || lang.id,
          words: lang.words || []
        }))
        if (this.languages.length === 0) {
          this.languages = []
        }
      } catch (error) {
        console.error('Error loading languages:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('GENERAL.Error'),
            icon: 'XIcon',
            variant: 'danger',
            text: this.$t('TRANSLATION_CONFIG.ErrorLoading') || 'Erro ao carregar configurações de tradução'
          }
        })
      } finally {
        this.isLoading = false
        this.loadingMessage = ''
      }
    },
    async saveAllLanguages() {
      this.isLoading = true
      this.loadingMessage = this.$t('TRANSLATION_CONFIG.Saving') || 'Salvando...'
      try {
        const languagesToSave = this.languages.map(lang => ({
          code: lang.code,
          label: lang.label,
          words: lang.words || []
        }))
        await translationConfigService.saveAllTranslationConfigs({ languages: languagesToSave })
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('GENERAL.Success'),
            icon: 'CheckIcon',
            variant: 'success',
            text: this.$t('TRANSLATION_CONFIG.SavedSuccessfully') || 'Configurações salvas com sucesso!'
          }
        })
        await this.loadLanguages()
      } catch (error) {
        console.error('Error saving languages:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('GENERAL.Error'),
            icon: 'XIcon',
            variant: 'danger',
            text: error.response?.data?.message || this.$t('TRANSLATION_CONFIG.ErrorSaving') || 'Erro ao salvar configurações'
          }
        })
      } finally {
        this.isLoading = false
        this.loadingMessage = ''
      }
    },
    addTerm() {
      const words = this.languages[this.activeTab].config?.words || this.languages[this.activeTab].words || []
      words.push({
        key: '',
        meaning: ''
      })
      if (this.languages[this.activeTab].config) {
        this.languages[this.activeTab].config.words = words
      } else {
        this.$set(this.languages[this.activeTab], 'words', words)
      }
    },
    removeTerm(index) {
      const words = this.languages[this.activeTab].config?.words || this.languages[this.activeTab].words || []
      words.splice(index, 1)
      if (this.languages[this.activeTab].config) {
        this.languages[this.activeTab].config.words = words
      } else {
        this.$set(this.languages[this.activeTab], 'words', words)
      }
    },
    openNewLanguageModal() {
      this.showNewLanguageModal = true
    },
    addNewLanguage(languageData) {
      this.languages.push({
        code: languageData.code,
        label: languageData.label,
        words: []
      })
      this.activeTab = this.languages.length - 1
      this.showNewLanguageModal = false
    },
    deleteLanguage() {
      this.showDeleteModal = true
    },
    async confirmDeleteLanguage() {
      const langToDelete = this.languages[this.activeTab]
      if (langToDelete._id) {
        this.isLoading = true
        this.loadingMessage = this.$t('TRANSLATION_CONFIG.Deleting') || 'Excluindo...'
        try {
          await translationConfigService.deleteTranslationConfig(langToDelete._id)
          this.$toast({
            component: ToastificationContent,
            props: {
              title: this.$t('GENERAL.Success'),
              icon: 'CheckIcon',
              variant: 'success',
              text: this.$t('TRANSLATION_CONFIG.DeletedSuccessfully') || 'Idioma excluído com sucesso!'
            }
          })
        } catch (error) {
          console.error('Error deleting language:', error)
          this.$toast({
            component: ToastificationContent,
            props: {
              title: this.$t('GENERAL.Error'),
              icon: 'XIcon',
              variant: 'danger',
              text: error.response?.data?.message || this.$t('TRANSLATION_CONFIG.ErrorDeleting') || 'Erro ao excluir idioma'
            }
          })
          this.showDeleteModal = false
          this.isLoading = false
          return
        } finally {
          this.isLoading = false
        }
      }
      this.languages.splice(this.activeTab, 1)
      if (this.activeTab >= this.languages.length) {
        this.activeTab = Math.max(0, this.languages.length - 1)
      }
      this.showDeleteModal = false
    }
  }
}
</script>

<style lang="scss">
.translation-config-tabs {
  .card {
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    border-radius: 0.5rem;
  }
  .card-header {
    background-color: #f8f9fa;
    border-bottom: 1px solid #dee2e6;
  }
}
.dark-layout .translation-config-tabs {
  .card-header, .nav-link {
    background-color: #343a40 !important; /* Fundo escuro para modo dark */
    border-bottom: 1px solid #dee2e6 !important;
  }
  .nav-tabs .nav-link {
    color: #adb5bd; /* Cor do texto para modo dark */
  }
  .nav-tabs .nav-link.active {
    background-color: #495057; /* Fundo da aba ativa */
    color: #fff; /* Cor do texto da aba ativa */
    border-color: #495057 #495057 #343a40; /* Bordas da aba ativa */
  }
}
</style>
